import { Navigate, Route, createRoutesFromElements } from "react-router";
import { ErrorScreen } from "@lending/components/ErrorScreen/ErrorScreen";
import { autoLoanRoutes, olbAutoLoanRoutes } from "./autoLoanRoutes";
import { olbCreditCardRoutes, webCreditCardRoutes } from "./creditCardRoutes";
import { ErrorBoundary } from "./ErrorBoundary";
import { type LendingAuthMode } from "../contexts/LendingHostContext";
import { LoanPortalLayout } from "../portal/LoanPortalLayout";
import { AutoLoanGuard } from "./guards/AutoLoanGuard";
import { BankingFlowGuard } from "./guards/BankingFlowGuard";
import { CreditCardGuard } from "./guards/CreditCardGuard";
import { ExistingApplicationGuard } from "./guards/ExistingApplicationGuard";
import { LandingPageGuard } from "./guards/LandingPageGuard";
import { PersonalLoanGuard } from "./guards/PersonalLoanGuard";
import { PortalLoginGuard } from "./guards/PortalLoginGuard";
import { WebFlowGuard } from "./guards/WebFlowGuard";
import { olbPersonalLoanRoutes, webPersonalLoanRoutes } from "./personalLoanRoutes";
import { ProductSelectionRedirect } from "./ProductSelectionRedirect";
import { RootRedirect } from "./RootRedirect";
import { IdentityScreen } from "../pages/PortalLogin/IdentityScreen";
import { PortalLoginLayout } from "../pages/PortalLogin/PortalLoginLayout";
import { SelectEmailScreen } from "../pages/PortalLogin/SelectEmailScreen";
import { VerifyCodeScreen } from "../pages/PortalLogin/VerifyCodeScreen";

const WEB_LOAN_ALLOWED_PAGES = ["eligibility", "questions", "get-started", "personal-info"];

const buildRoutes = (authMode: LendingAuthMode) =>
  createRoutesFromElements(
    <Route errorElement={<ErrorBoundary />}>
      <Route path="/" element={<RootRedirect />} />
      <Route path="/error" element={<ErrorScreen />} />

      <Route element={<PortalLoginGuard requireChallenge={false} />}>
        {/* Loan Portal dashboard */}
        <Route path="/portal" element={<LoanPortalLayout />} />

        {/* Loan Portal verified identity login. Absent when hosted rather than merely
          unreachable: the login proves identity by email, which is meaningless for a user
          Online Banking has already authenticated through MFA. Falls through to the catch-all
          below, so a stale link lands on the root redirect instead of an identity challenge.
          createRoutesFromElements ignores non-element children, so `false` is fine here. */}
        {authMode !== "digital_banking" && (
          <Route path="/portal/login" element={<PortalLoginLayout />}>
            <Route index element={<IdentityScreen />} />
            <Route element={<PortalLoginGuard />}>
              <Route path="select-email" element={<SelectEmailScreen />} />
              <Route path="verify" element={<VerifyCodeScreen />} />
            </Route>
          </Route>
        )}
      </Route>

      {/* Web Flow Routes */}
      <Route element={<WebFlowGuard />}>
        <Route path="/product-selection" element={<ProductSelectionRedirect />} />

        {/* Web Personal Loan */}
        <Route path="/personal-loan" element={<PersonalLoanGuard />}>
          <Route
            element={
              <LandingPageGuard
                allowedPages={WEB_LOAN_ALLOWED_PAGES}
                fallbackPath="/product-selection"
              />
            }
          >
            <Route
              element={
                <ExistingApplicationGuard
                  productType="personal-loan"
                  allowedPages={WEB_LOAN_ALLOWED_PAGES}
                />
              }
            >
              {webPersonalLoanRoutes}
            </Route>
          </Route>
        </Route>

        {/* Web Credit Card */}
        <Route path="/credit-card" element={<CreditCardGuard />}>
          <Route
            element={
              <LandingPageGuard
                allowedPages={["eligibility", "questions", "get-started", "select-card"]}
                fallbackPath="/product-selection"
              />
            }
          >
            <Route
              element={
                <ExistingApplicationGuard
                  productType="credit-card"
                  allowedPages={["eligibility", "questions", "get-started", "select-card"]}
                />
              }
            >
              {webCreditCardRoutes}
            </Route>
          </Route>
        </Route>

        {/* Web Auto Loan */}
        <Route path="/auto-loan" element={<AutoLoanGuard />}>
          <Route
            element={
              <LandingPageGuard
                allowedPages={WEB_LOAN_ALLOWED_PAGES}
                fallbackPath="/product-selection"
              />
            }
          >
            <Route
              element={
                <ExistingApplicationGuard
                  productType="auto-loan"
                  allowedPages={WEB_LOAN_ALLOWED_PAGES}
                />
              }
            >
              {autoLoanRoutes}
            </Route>
          </Route>
        </Route>
      </Route>

      {/* Banking Flow Routes */}
      <Route element={<BankingFlowGuard />}>
        {/* Banking Personal Loan */}
        <Route path="/olb-personal-loan" element={<PersonalLoanGuard />}>
          <Route
            element={
              <LandingPageGuard allowedPages={["confirm-info"]} fallbackPath="confirm-info" />
            }
          >
            <Route
              element={
                <ExistingApplicationGuard
                  productType="personal-loan"
                  allowedPages={["confirm-info"]}
                />
              }
            >
              {olbPersonalLoanRoutes}
            </Route>
          </Route>
        </Route>

        {/* Banking Credit Card */}
        <Route path="/olb-credit-card" element={<CreditCardGuard />}>
          <Route
            element={
              <LandingPageGuard allowedPages={["confirm-info"]} fallbackPath="confirm-info" />
            }
          >
            <Route
              element={
                <ExistingApplicationGuard
                  productType="credit-card"
                  allowedPages={["confirm-info"]}
                />
              }
            >
              {olbCreditCardRoutes}
            </Route>
          </Route>
        </Route>

        {/* Banking Auto Loan */}
        <Route path="/olb-auto-loan" element={<AutoLoanGuard />}>
          <Route
            element={
              <LandingPageGuard allowedPages={["confirm-info"]} fallbackPath="confirm-info" />
            }
          >
            <Route
              element={
                <ExistingApplicationGuard productType="auto-loan" allowedPages={["confirm-info"]} />
              }
            >
              {olbAutoLoanRoutes}
            </Route>
          </Route>
        </Route>
      </Route>

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Route>
  );

/**
 * Router-free accessor for the lending route tree. This is the single entry
 * point for the routes: the standalone `App` builds a browser router from it,
 * and a host (e.g. azul) can mount the same tree in its own router.
 *
 * The host's auth mode shapes the tree, so pass the same value given to
 * `LendingProviders`; routes that only make sense for a standalone visitor are
 * omitted when hosted.
 */
export function getLendingRoutes(authMode: LendingAuthMode = "lending_session") {
  return buildRoutes(authMode);
}
